import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { API } from "../App";
import { toast } from "sonner";
import { FileSignature, Plus, Eye, Ban, Loader2, Copy } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  signed: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  voided: "bg-slate-200 text-slate-600",
};
const DOC_TYPES = [
  { v: "career_plan", l: "Career Plan" },
  { v: "agreement", l: "Training Agreement" },
  { v: "loi", l: "Letter of Intent" },
  { v: "other", l: "Other" },
];

const ESignaturesTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({ participant_user_id: "", title: "", document_type: "agreement", body_text: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/signature-requests`, { credentials: "include" });
      const d = await res.json();
      setRequests(d.requests || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/signature-requests`, { credentials: "include" });
        const d = await res.json();
        setRequests(d.requests || []);
      } catch { /* ignore */ } finally { setLoading(false); }
      try {
        const r2 = await fetch(`${API}/users`, { credentials: "include" });
        const d2 = await r2.json();
        setParticipants(Array.isArray(d2) ? d2 : []);
      } catch { /* ignore */ }
    })();
  }, []);

  const create = async () => {
    if (!form.participant_user_id || !form.title.trim() || !form.body_text.trim()) {
      toast.error("Pick a participant, add a title and agreement text"); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/signature-requests`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Failed");
      toast.success("Signature request sent");
      setCreateOpen(false);
      setForm({ participant_user_id: "", title: "", document_type: "agreement", body_text: "" });
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const voidReq = async (r) => {
    if (!window.confirm(`Void the request "${r.title}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/signature-requests/${r.request_id}/void`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success("Voided"); load();
    } catch { toast.error("Failed to void"); }
  };

  const viewReq = async (r) => {
    try {
      const res = await fetch(`${API}/admin/signature-requests/${r.request_id}`, { credentials: "include" });
      setViewing(await res.json());
    } catch { toast.error("Failed to load"); }
  };

  const copyLink = (r) => {
    const url = `${window.location.origin}/sign/${r.request_id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Sign link copied");
  };

  return (
    <Card className="card-surface" data-testid="esignatures-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-slate-900"><FileSignature className="w-5 h-5 text-brand-navy" /> E-Signatures</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Send career plans, agreements and LOIs to participants for a legally-binding e-signature.</p>
        </div>
        <Button className="btn-primary gap-2" onClick={() => setCreateOpen(true)} data-testid="new-signature-btn"><Plus className="w-4 h-4" /> New request</Button>
      </CardHeader>
      <CardContent>
        {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Document</TableHead><TableHead>Participant</TableHead><TableHead>Type</TableHead>
                <TableHead>Status</TableHead><TableHead>Sent</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {requests.map(r => (
                  <TableRow key={r.request_id} data-testid={`sig-row-${r.request_id}`}>
                    <TableCell className="font-medium text-slate-800">{r.title}</TableCell>
                    <TableCell>{r.participant_name}<div className="text-xs text-slate-400">{r.participant_email}</div></TableCell>
                    <TableCell className="capitalize">{(r.document_type || "").replace(/_/g, " ")}</TableCell>
                    <TableCell><Badge className={STATUS_STYLE[r.status] || ""} data-testid={`sig-status-${r.request_id}`}>{r.status}</Badge></TableCell>
                    <TableCell className="text-slate-500 text-sm">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="btn-outline h-8" onClick={() => viewReq(r)} data-testid={`sig-view-${r.request_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        {r.status === "pending" && <>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => copyLink(r)} data-testid={`sig-copy-${r.request_id}`}><Copy className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600" onClick={() => voidReq(r)} data-testid={`sig-void-${r.request_id}`}><Ban className="w-3.5 h-3.5" /></Button>
                        </>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!requests.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No signature requests yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="bg-white" data-testid="new-signature-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">New signature request</DialogTitle>
            <DialogDescription className="text-slate-500">The participant gets a secure link to review and sign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1"><Label className="text-slate-700">Participant *</Label>
              <Select value={form.participant_user_id} onValueChange={(v) => setForm(f => ({ ...f, participant_user_id: v }))}>
                <SelectTrigger className="input-base" data-testid="sig-participant-select"><SelectValue placeholder="Choose a participant" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {participants.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-slate-700">Title *</Label>
                <Input className="input-base" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Training Agreement" data-testid="sig-title" /></div>
              <div className="space-y-1"><Label className="text-slate-700">Type</Label>
                <Select value={form.document_type} onValueChange={(v) => setForm(f => ({ ...f, document_type: v }))}>
                  <SelectTrigger className="input-base" data-testid="sig-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{DOC_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-slate-700">Agreement text *</Label>
              <Textarea className="input-base min-h-[140px]" value={form.body_text} onChange={(e) => setForm(f => ({ ...f, body_text: e.target.value }))} placeholder="Paste the agreement / career plan text the participant will read and sign…" data-testid="sig-body" /></div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="btn-outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
              <Button className="btn-primary gap-2" onClick={create} disabled={saving} data-testid="sig-send-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Send for signature</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View modal */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="view-signature-dialog">
          <DialogHeader><DialogTitle className="text-slate-900">{viewing?.title}</DialogTitle>
            <DialogDescription className="text-slate-500">{viewing?.participant_name}</DialogDescription></DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <Badge className={STATUS_STYLE[viewing.status]}>{viewing.status}</Badge>
              {viewing.body_text && <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3 max-h-48 overflow-y-auto whitespace-pre-wrap">{viewing.body_text}</div>}
              {viewing.status === "signed" && (
                <div className="text-sm text-slate-600 space-y-1 border-t border-slate-200 pt-3">
                  <p><span className="text-slate-400">Signed by:</span> {viewing.signer_name}</p>
                  <p><span className="text-slate-400">Signed at:</span> {viewing.signed_at && new Date(viewing.signed_at).toLocaleString()}</p>
                  <p><span className="text-slate-400">IP:</span> {viewing.signer_ip}</p>
                  {viewing.signature_image && <img src={viewing.signature_image} alt="signature" className="border border-slate-200 rounded bg-white max-h-24 mt-2" />}
                </div>
              )}
              {viewing.status === "declined" && <p className="text-sm text-rose-600">Declined: {viewing.decline_reason || "(no reason given)"}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ESignaturesTab;
